import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismBanner, PrismInput } from '@mylighthouse/prism-ember';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { t } from 'ember-intl';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import GeneralFormsFormFieldsDatepickerFormField from 'lumen/components/general/forms/form-fields/datepicker-form-field';
import {
  MAX_LENGTH_KEY_DATE_NOTE,
  OD_KEY_DATES_SOURCES,
} from 'lumen/utils/constants/overview-dashboard-constants';

interface HotelOverviewDashboardModalsAddKeyDateSignature {
  Args: {
    alertsResponseData: unknown;
    closeModal: unknown;
    relaunchAlertsDataRequest: unknown;
  };
}

export default class HotelOverviewDashboardModalsAddKeyDate extends Component<HotelOverviewDashboardModalsAddKeyDateSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare toast: Services['toast'];

  @tracked keyDateNote = '';
  @tracked selectedDate = null;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerMaxDate() {
    return this.hotelAndMarketService.getLocalTime().add(1, 'year');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get datePickerMinDate() {
    return this.hotelAndMarketService.getLocalTime();
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isConfirmDisabled() {
    // Can't create a key date if the user hasn't picked a day yet, or has too long note, or the key date already exist
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      this.selectedDate === null ||
      this.showDuplicateKeyDateWarning ||
      (this.keyDateNote !== '' &&
        this.keyDateNote.length > MAX_LENGTH_KEY_DATE_NOTE)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showDuplicateKeyDateWarning() {
    if (
      this.args.alertsResponseData &&
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.alertsResponseData.keyDates.length
    ) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      return this.args.alertsResponseData.keyDates.find(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        (keyDate) => keyDate.fromDate === this.selectedDate,
      );
    }

    return false;
  }

  @action
  async addKeyDate(): Promise<void> {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const result = await this.args.createKeyDateTask.perform(
      this.selectedDate,
      this.keyDateNote,
      OD_KEY_DATES_SOURCES.manual,
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (result.isSuccess) {
      this.toast.success(
        this.intl.t(
          'overview_dashboard.key_date.add_key_date.message.success.title',
        ),
        { timeout: TOAST_TIMEOUT_LONG },
      );

      // Refresh the feed since a key date has been added
      // @ts-expect-error: Incorrect type
      this.args.relaunchAlertsDataRequest();

      // @ts-expect-error: Incorrect type
      this.args.closeModal();
    } else if (
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      result.error.payload?.errors[0].detail === 'key date already exists'
    ) {
      // OD TO DO: find better way to handle this - should be in CONST as well?
      // Wasn't high prio enough before release. Can be cleaned up later
      // Close the modal as the manually added day is already a key date
      // @ts-expect-error: Incorrect type
      this.args.closeModal();

      this.toast.alert(
        this.intl.t(
          'overview_dashboard.key_date.add_key_date.message.duplicate.title',
        ),
        { timeout: TOAST_TIMEOUT_LONG },
      );
    } else {
      this.toast.alert(
        this.intl.t(
          'overview_dashboard.key_date.add_key_date.message.error.title',
        ),
        { timeout: TOAST_TIMEOUT_LONG },
      );
    }
  }

  @action
  // @ts-expect-error: Incorrect type
  onSelectDate(value): void {
    if (value) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      this.selectedDate = value.format('YYYY-MM-DD');
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.selectedDate = value;
    }
  }

  // @ts-expect-error: Incorrect type
  @action updateKeyDateNote(event): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.keyDateNote = event.target.value;
  }

  <template>
    <div class="modal--padded flex flex-col">
      {{#if this.showDuplicateKeyDateWarning}}
        <div class="mb-400">
          {{! @glint-expect-error: Incorrect type }}
          <PrismBanner
            @bannerStyle="warning"
            @bannerTitle={{t
              "overview_dashboard.key_date.add_key_date.message.duplicate.title"
            }}
          />
        </div>
      {{/if}}

      <div class="w-1/4">
        <GeneralFormsFormFieldsDatepickerFormField
          {{! @glint-expect-error: Incorrect type }}
          @datePickerMaxDate={{this.datePickerMaxDate}}
          @datePickerMinDate={{this.datePickerMinDate}}
          @label={{t "general.select_a_day"}}
          @onSelectDate={{this.onSelectDate}}
          @selectedDate={{this.selectedDate}}
        />
      </div>

      <PrismInput
        @label={{t "overview_dashboard.feed.note.title"}}
        @maxLength={{MAX_LENGTH_KEY_DATE_NOTE}}
        @onInput={{this.updateKeyDateNote}}
        @placeholder={{t
          "overview_dashboard.key_date.modal.input.reminder.placeholder"
        }}
        @rows={{5}}
        @textarea={{true}}
        @value={{this.keyDateNote}}
      />
    </div>
    <div class="settings-modal__footer">
      <GeneralFormFieldsCancelAndConfirm
        {{! @glint-expect-error: Incorrect type }}
        @cancel={{@closeModal}}
        @confirm={{this.addKeyDate}}
        @confirmText={{t "overview_dashboard.key_date.button.add_key_date"}}
        @isConfirmDisabled={{this.isConfirmDisabled}}
      />
    </div>
  </template>
}
